import { useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { ReceiptText, Check, ArrowUpRight, ChevronRight } from "lucide-react";
import { Navbar } from "./Navbar";
import { Contatti } from "./Contatti";
import { Footer } from "./Footer";
import { Reveal, Eyebrow } from "./Reveal";
import { SERVICES, PROCESS_STEPS, DOMAIN } from "../data/services";
import { useLang } from "../i18n";

const DEFAULT_TITLE = "Impresa Edile a Bologna | Adriatica S.r.l. Semplificata";
const DEFAULT_DESC =
  "Adriatica S.r.l. Semplificata: impresa edile a Bologna con oltre 20 anni di esperienza. Ristrutturazioni, nuove costruzioni, impianti e lavori chiavi in mano.";

export default function ServicePage() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const s = SERVICES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!s) return;
    document.title = s.metaTitle;
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", s.metaDescription);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${DOMAIN}/servizi/${slug}`);
    return () => {
      document.title = DEFAULT_TITLE;
      if (md) md.setAttribute("content", DEFAULT_DESC);
      if (canonical) canonical.setAttribute("href", `${DOMAIN}/`);
    };
  }, [slug, s]);

  if (!s) return <Navigate to="/" replace />;

  const en = lang === "en";
  const body = en ? s.bodyEn : s.body;
  const includes = en ? s.includesEn : s.includes;

  return (
    <div className="grain bg-[#070D18] min-h-screen" data-testid={`service-page-${slug}`}>
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img src={s.heroImg} alt={s.heroAlt} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#070D18] via-[#070D18]/85 to-[#070D18]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#070D18] via-transparent to-[#070D18]/70" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="service-breadcrumb">
              <a href="/" className="hover:text-[#F3E08B] transition-colors">Home</a>
              <ChevronRight size={12} className="text-[#D4AF37]" />
              <a href="/#servizi" className="hover:text-[#F3E08B] transition-colors">{t("Servizi", "Services")}</a>
              <ChevronRight size={12} className="text-[#D4AF37]" />
              <span className="text-[#F3E08B]">{en ? s.breadcrumbEn : s.breadcrumb}</span>
            </nav>
            <h1 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight max-w-3xl">
              {en ? s.h1En : s.h1}
            </h1>
            <p className="mt-5 font-serif italic text-xl sm:text-2xl text-gold-gradient max-w-2xl">{en ? s.introEn : s.intro}</p>
          </div>
        </section>

        <section className="relative py-16 sm:py-20">
          <div className="absolute inset-0 blueprint-dots opacity-[0.04] pointer-events-none" />
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal>
                <Eyebrow>{t("Il servizio", "The service")}</Eyebrow>
                {body.map((p, i) => (
                  <p key={i} className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed">
                    {p}
                  </p>
                ))}
              </Reveal>
              {s.detrazioni && (
                <Reveal delay={0.15}>
                  <div
                    data-testid="service-detrazioni-callout"
                    className="mt-8 rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/15 to-transparent p-6 flex gap-4"
                  >
                    <ReceiptText className="text-[#D4AF37] shrink-0 mt-0.5" size={22} />
                    <p className="text-sm sm:text-base text-slate-200 leading-relaxed">
                      <span className="font-semibold text-[#F3E08B]">{t("Detrazioni fiscali.", "Tax deductions.")}</span>{" "}
                      {t(
                        "Molti lavori di ristrutturazione danno diritto alle detrazioni fiscali previste dalla normativa vigente: ti affianchiamo nella gestione della documentazione.",
                        "Many renovation works qualify for the tax deductions provided by current regulations: we support you with the paperwork."
                      )}
                    </p>
                  </div>
                </Reveal>
              )}
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="trace-card rounded-2xl bg-[#0E1726] p-7 sm:p-8" data-testid="service-includes-card">
                  <h2 className="font-serif text-2xl font-medium text-white">{t("Cosa comprende", "What's included")}</h2>
                  <ul className="mt-6 flex flex-col gap-4">
                    {includes.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm sm:text-base text-slate-300">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/40 shrink-0 mt-0.5">
                          <Check size={13} className="text-[#D4AF37]" />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a
                    href="/#contatti"
                    data-testid="service-cta-quote"
                    className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#070D18] bg-gradient-to-r from-[#D4AF37] to-[#F3E08B] px-6 py-3 rounded-full hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow duration-300"
                  >
                    {t("Richiedi un preventivo", "Request a quote")}
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="relative py-16 sm:py-20 bg-[#0A1220]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <Eyebrow>{t("Come lavoriamo", "How we work")}</Eyebrow>
              <h2 className="mt-4 font-display font-semibold text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight">
                {t("Dal primo contatto alla consegna", "From first contact to delivery")}
              </h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="service-process-steps">
              {PROCESS_STEPS.map((step, i) => (
                <Reveal key={step.n} delay={0.07 * i}>
                  <div className="trace-card h-full rounded-2xl bg-[#0E1726] p-6">
                    <span className="font-display text-3xl text-gold-gradient font-bold">{step.n}</span>
                    <h3 className="mt-4 font-serif text-xl font-medium text-white">{en ? step.titleEn : step.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{en ? step.textEn : step.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <Contatti />
      </main>
      <Footer />
    </div>
  );
}
